import React, { useState } from 'react';
import Card from './Card';
import Badge from './Badge';
import Button from './Button';
import Input from './Input';

export interface SigningRequestSummary {
  id: string;
  template_key: string;
  person_id: string | null;
  company_id: string | null;
  project_id: string | null;
  status: 'sent' | 'signed' | 'expired' | 'revoked';
  expires_at: string | null;
  signer_name: string | null;
  signer_email: string | null;
  signed_at: string | null;
  resulting_document_id: string | null;
  created_at: string;
  updated_at: string;
}

const TEMPLATE_LABELS: Record<string, string> = {
  nda: 'NDA',
  proposal_acceptance: 'Proposal Acceptance',
  statement_of_work: 'Statement of Work',
  partnership_agreement: 'Partnership Agreement',
};

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  if (status === 'signed') return 'success';
  if (status === 'revoked') return 'danger';
  if (status === 'expired') return 'warning';
  return 'default'; // sent
}

interface Props {
  personId?: string;
  companyId?: string;
  projectId?: string;
  requests: SigningRequestSummary[];
  onChanged: () => void | Promise<void>;
}

// Shared across Person/Company/Project detail pages — see project-brain/pages/{people,companies,projects}-detail.md.
const SigningRequests: React.FC<Props> = ({ personId, companyId, projectId, requests, onChanged }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateKey, setTemplateKey] = useState('nda');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [signerAddress, setSignerAddress] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  function openDialog() {
    setDialogOpen(true);
    setCreatedUrl(null);
    setCreateError(null);
    setTemplateKey('nda');
    setSignerName('');
    setSignerEmail('');
    setSignerRole('');
    setSignerAddress('');
    setCopied(false);
  }

  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    try {
      const prefill_data: Record<string, string> = {};
      if (signerName.trim()) prefill_data.signer_name = signerName.trim();
      if (signerEmail.trim()) prefill_data.signer_email = signerEmail.trim();
      if (signerRole.trim()) prefill_data.signer_role = signerRole.trim();
      if (signerAddress.trim()) prefill_data.signer_address = signerAddress.trim();

      const res = await fetch('/api/signing-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_key: templateKey,
          person_id: personId,
          company_id: companyId,
          project_id: projectId,
          prefill_data,
        }),
      });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      // Deliberately do NOT call onChanged() here yet: the parent page's refresh
      // remounts this component (full-page loading state), which would wipe the
      // one-time link reveal below before the admin has a chance to copy it.
      // onChanged() runs when they dismiss the reveal via "Done" instead.
      setCreatedUrl(json.data.url);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create signing link.');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this signing link? The client will no longer be able to use it.')) return;
    setRevokingId(id);
    setRevokeError(null);
    try {
      const res = await fetch(`/api/signing-requests/${id}`, { method: 'DELETE' });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      await onChanged();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke signing link.');
    } finally {
      setRevokingId(null);
    }
  }

  function copyLink() {
    if (!createdUrl) return;
    navigator.clipboard
      ?.writeText(createdUrl)
      .then(() => setCopied(true))
      .catch(() => {
        // Clipboard permission can fail silently — the link is still selectable in the input below.
      });
  }

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h2 className="section-title" style={{ margin: 0 }}>Signing requests</h2>
        {!dialogOpen && (
          <Button variant="secondary" size="sm" onClick={openDialog}>
            Create signing link
          </Button>
        )}
      </div>

      {requests.length === 0 ? (
        <p className="detail-field-empty">No signing requests yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {requests.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem 0',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div>
                <span className="detail-project-name">{TEMPLATE_LABELS[r.template_key] ?? r.template_key}</span>
                <span style={{ marginLeft: '0.5rem' }}>
                  <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                </span>
                {r.signer_name && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.8125rem', color: '#64748b' }}>
                    {r.signer_name}
                  </span>
                )}
              </div>
              {r.status === 'sent' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRevoke(r.id)}
                  disabled={revokingId === r.id}
                >
                  {revokingId === r.id ? 'Revoking…' : 'Revoke'}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {revokeError && <p className="edit-save-error">{revokeError}</p>}

      {dialogOpen && (
        <div className="edit-form" style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
          {createdUrl ? (
            <>
              <p style={{ fontSize: '0.875rem', color: '#0f766e', fontWeight: 600 }}>
                Signing link created — copy it now, it can't be shown again.
              </p>
              <div className="input-wrapper">
                <input
                  className="input"
                  value={createdUrl}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                />
              </div>
              <div className="edit-actions">
                <Button variant="primary" onClick={copyLink}>
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDialogOpen(false);
                    onChanged();
                  }}
                >
                  Done
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="input-wrapper">
                <label className="input-label">Template</label>
                <select
                  className="input"
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                  disabled={creating}
                >
                  <option value="nda">NDA</option>
                  <option value="proposal_acceptance">Proposal Acceptance</option>
                  <option value="statement_of_work">Statement of Work</option>
                  <option value="partnership_agreement">Partnership Agreement</option>
                </select>
              </div>
              <Input
                label="Signer name (optional override)"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                disabled={creating}
              />
              <Input
                label="Signer email (optional override)"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                disabled={creating}
              />
              <Input
                label="Signer role (optional)"
                value={signerRole}
                onChange={(e) => setSignerRole(e.target.value)}
                disabled={creating}
              />
              <Input
                label="Signer address (optional)"
                value={signerAddress}
                onChange={(e) => setSignerAddress(e.target.value)}
                disabled={creating}
              />

              {createError && <p className="edit-save-error">{createError}</p>}

              <div className="edit-actions">
                <Button variant="primary" onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating…' : 'Create link'}
                </Button>
                <Button variant="secondary" onClick={() => setDialogOpen(false)} disabled={creating}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default SigningRequests;
